import React, { useEffect, useState } from "react";
import { ActionBar, Button, HStack, Separator, Text, VStack } from "@chakra-ui/react";
import { useAppStore } from "../../../context/store";
import { Tooltip } from "../../ui/tooltip";
import HoAssociation from "../../../icons/HoAssociation";
import Type from "../../../utilies/Types";
import { LuArrowBigUpDash, LuUndo, LuX } from "react-icons/lu";
import { createAssociationLayerClickHandler } from "../../../utilies/association/CreateAssociation";
import { createAutoAssociationLayerClickHandler } from "../../../utilies/association/CreateAutoAssociation";
import { useMapObjects } from "../../../context/MapObjectsContext";
import { getCombinedKeysText } from "../../../utilies/Utility";

function Association() {
  // state
  const viewerRef = useAppStore((s) => s.viewerRef);
  const association = useAppStore((s) => s.association);
  const fastAssociation = useAppStore((s) => s.fastAssociation);
  const originalCameraControls = useAppStore((s) => s.originalCameraControls);
  const editMode = useAppStore((s) => s.editMode);
  const savedHotkeysRef = useAppStore((s) => s.savedHotkeysRef);
  const userCanEditing = useAppStore((s) => s.userCanEditing);
  const userData = useAppStore((s) => s.userData);

  // setters
  const { setMapObjects, mapObjectsRef } = useMapObjects();
  const setAssociation = useAppStore.getState().setAssociation;
  const setFastAssociation = useAppStore.getState().setFastAssociation;
  const setCanHightlightEntity = useAppStore.getState().setCanHightlightEntity;

  const [associationHandler, setAssociationHandler] = useState(null);

  useEffect(() => {
    const isMod = (k) => ["shift", "ctrl", "alt", "meta"].includes(String(k).toLowerCase());
    const isAlpha = (k) => /^[a-z]$/i.test(String(k || ""));
    const normalizeKey = (k) => {
      let key = String(k || "").toLowerCase();
      if (key === "control") key = "ctrl";
      if (key === " ") key = "space";
      return key;
    };

    const getCombo = (type) => savedHotkeysRef.current.find((h) => h.type === type)?.combination || [];

    const matches = (e, combo) => {
      if (!combo || combo.length === 0) return false;

      const norm = combo.map(normalizeKey);

      const want = {
        shift: norm.includes("shift"),
        ctrl: norm.includes("ctrl"),
        alt: norm.includes("alt"),
        meta: norm.includes("meta"),
      };

      if (e.shiftKey !== want.shift) return false;
      if (e.ctrlKey !== want.ctrl) return false;
      if (e.altKey !== want.alt) return false;
      if (e.metaKey !== want.meta) return false;

      const main = norm.find((k) => !isMod(k));
      if (!main) return true;

      const pressed = normalizeKey(e.key);
      return pressed === main;
    };

    // -------------------------
    // 3-key sequential support
    // combo: [mod, letter1, letter2]
    // user: mod+letter1, then within 600ms press letter2 (with same mods or no mods)
    // -------------------------
    const PENDING_KEY = "__association_hotkey_pending__";
    if (!window[PENDING_KEY]) {
      window[PENDING_KEY] = {
        active: false,
        type: null,
        mods: [],
        stepKey: "",
        finalKey: "",
        expiresAt: 0,
        tid: null,
      };
    }
    const pending = window[PENDING_KEY];

    const resetPending = () => {
      if (pending.tid) clearTimeout(pending.tid);
      pending.active = false;
      pending.type = null;
      pending.mods = [];
      pending.stepKey = "";
      pending.finalKey = "";
      pending.expiresAt = 0;
      pending.tid = null;
    };

    const modsMatchExactly = (e, mods) => {
      const needShift = mods.includes("shift");
      const needCtrl = mods.includes("ctrl");
      const needAlt = mods.includes("alt");
      const needMeta = mods.includes("meta");

      if (e.shiftKey !== needShift) return false;
      if (e.ctrlKey !== needCtrl) return false;
      if (e.altKey !== needAlt) return false;
      if (e.metaKey !== needMeta) return false;
      return true;
    };

    const modsAreNone = (e) => !e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey;

    const isSequential3Combo = (combo) => {
      if (!combo || combo.length !== 3) return false;

      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));

      return mods.length === 1 && nonMods.length === 2 && isAlpha(nonMods[0]) && isAlpha(nonMods[1]);
    };

    const getSequentialParts = (combo) => {
      const norm = combo.map(normalizeKey);
      const mods = norm.filter(isMod);
      const nonMods = norm.filter((k) => !isMod(k));
      return { mods, stepKey: nonMods[0], finalKey: nonMods[1] };
    };

    const startPending = (type, mods, stepKey, finalKey) => {
      resetPending();
      pending.active = true;
      pending.type = type;
      pending.mods = mods;
      pending.stepKey = stepKey;
      pending.finalKey = finalKey;
      pending.expiresAt = Date.now() + 3000;
      pending.tid = setTimeout(() => resetPending(), 3000);
    };

    const runAction = (type, e) => {
      if (type === Type.HOTKEYS.TOGGLE_ASSOCIATION) {
        e.preventDefault();
        if (!userCanEditing) return true;
        handleToggleAssociation();
        return true;
      }

      if (type === Type.HOTKEYS.TOGGLE_ASSOCIATION_FAST) {
        e.preventDefault();
        if (!userCanEditing) return true;
        handleToggleFastAssociation();
        return true;
      }

      return false;
    };

    const handleKeyDown = (e) => {
      if (editMode) return;

      const key = normalizeKey(e.key);

      if (key === "escape") {
        e.preventDefault();
        resetPending();
        setAssociation(false);
        setFastAssociation(false);
        return;
      }

      // 1) pending final key check
      if (pending.active) {
        if (Date.now() > pending.expiresAt) {
          resetPending();
        } else {
          const okMods = modsMatchExactly(e, pending.mods) || modsAreNone(e);

          if (okMods && key === normalizeKey(pending.finalKey)) {
            e.preventDefault();
            const type = pending.type;
            resetPending();
            runAction(type, e);
            return;
          }

          if (!isMod(key)) resetPending();
        }
      }

      // 2) check hotkeys (order preserved)
      const ordered = [Type.HOTKEYS.TOGGLE_ASSOCIATION, Type.HOTKEYS.TOGGLE_ASSOCIATION_FAST];

      for (const type of ordered) {
        const combo = getCombo(type);

        // sequential start
        if (isSequential3Combo(combo)) {
          const { mods, stepKey, finalKey } = getSequentialParts(combo);

          if (modsMatchExactly(e, mods) && key === normalizeKey(stepKey)) {
            e.preventDefault();
            startPending(type, mods, stepKey, finalKey);
            return;
          }
        }

        // normal one-shot
        if (matches(e, combo)) {
          resetPending();
          runAction(type, e);
          return;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      resetPending();
    };
  }, [editMode]);

  useEffect(() => {
    associationHandler?.destroy?.();
    setAssociationHandler(null);

    if (!viewerRef.current) {
      setCanHightlightEntity(true);
      return;
    }

    if (association) {
      const handler = createAssociationLayerClickHandler(viewerRef.current, null, () => {}, mapObjectsRef, setMapObjects, { area: true, originalCameraControls, userData });
      setAssociationHandler(handler);
      setCanHightlightEntity(false);
      return;
    }

    if (fastAssociation) {
      const handler = createAutoAssociationLayerClickHandler(viewerRef.current, null, () => {}, mapObjectsRef, setMapObjects, userData);
      setAssociationHandler(handler);
      setCanHightlightEntity(false);
      return;
    }

    setCanHightlightEntity(true);
  }, [association, fastAssociation]);

  const handleToggleAssociation = () => {
    if (!userCanEditing || editMode) return;
    setFastAssociation(false);
    setAssociation((prev) => !prev);
  };

  const handleToggleFastAssociation = () => {
    if (!userCanEditing || editMode) return;
    setAssociation(false);
    setFastAssociation((prev) => !prev);
  };

  const handleUndoAssociation = () => {
    associationHandler?.undoLast?.();
  };

  const handleCloseAssociationTools = () => {
    setAssociation(false);
    setFastAssociation(false);
  };

  const isToolOpen = association || fastAssociation;

  return (
    <>
      <HStack>
        <Button
          disabled={editMode}
          variant="solid"
          p={0}
          opacity={1}
          bg={association ? "green" : "white"}
          color={association ? "white" : "MenuText"}
          onClick={handleToggleAssociation}
          aria-pressed={association}
        >
          <Tooltip content={"Association " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.TOGGLE_ASSOCIATION)}>
            <HoAssociation />
          </Tooltip>
        </Button>
      </HStack>

      <ActionBar.Root open={isToolOpen} closeOnEscape={false} closeOnInteractOutside={false}>
        <ActionBar.Positioner>
          <ActionBar.Content>
            <VStack gap={0} align="flex-start">
              <HStack gap={2}>
                <Text fontWeight="semibold" fontSize="sm">
                  Association
                </Text>
              </HStack>

              <Separator my={2} width={"full"} />

              <HStack>
                {associationHandler && isToolOpen && (
                  <Button disabled={editMode || !associationHandler} p={0} bg={"white"} color={"MenuText"} borderWidth={1} borderColor={"gray.200"} onClick={handleUndoAssociation}>
                    <Tooltip content={"Undo"}>
                      <LuUndo size={22} />
                    </Tooltip>
                  </Button>
                )}

                <Button
                  disabled={editMode}
                  p={0}
                  bg={fastAssociation ? "green" : "white"}
                  color={fastAssociation ? "white" : "MenuText"}
                  borderWidth={1}
                  borderColor={fastAssociation ? "green" : "gray.200"}
                  onClick={handleToggleFastAssociation}
                  aria-pressed={fastAssociation}
                >
                  <Tooltip content={"Association " + getCombinedKeysText(savedHotkeysRef, Type.HOTKEYS.TOGGLE_ASSOCIATION_FAST)}>
                    <LuArrowBigUpDash size={22} />
                  </Tooltip>
                </Button>
                <Button p={0} variant={"outline"} colorPalette={"red"} onClick={handleCloseAssociationTools} aria-label="Close Association Tools">
                  <Tooltip content={"Disable"}>
                    <LuX size={18} />
                  </Tooltip>
                </Button>
              </HStack>
            </VStack>
          </ActionBar.Content>
        </ActionBar.Positioner>
      </ActionBar.Root>
    </>
  );
}

export default Association;
